import plusIcon from "../../assets/icons/plus.svg";
import { Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { showSuccess, showError } from "../../components/ui/toast";
import { getToken, getUser } from "../../services/authStore";
import "./category.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function CategoryModule() {
  const token = getToken();
  const user = getUser();

  const role = user?.role || "";

  const canShowGlobalToggle =
    role === "super_admin" || role === "support_admin";

  const [categories, setCategories] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [types, setTypes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isFirstSave, setIsFirstSave] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ================= FETCH TYPES =================
  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/settings/types/active/${user.company_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = res.data || [];

      // Company ID matching filter
      const filtered = data.filter(
        (t) => String(t.company_id) === String(user.company_id),
      );

      setTypes(filtered);
    } catch {
      showError("Failed to fetch types");
    }
  }, [token, user.company_id]);

  // ================= FETCH CATEGORIES =================
  const fetchCategories = useCallback(async () => {
    if (!selectedType) return;

    try {
      const res = await axios.get(
        `${API_URL}/settings/categories/${user.company_id}/${selectedType}/${role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = res.data || [];

      setIsFirstSave(data.length === 0);

      setCategories(
        data.map((c) => ({
          id: c.id,
          name: c.name || "",
          color: c.colour
            ? c.colour.startsWith("#")
              ? c.colour
              : `#${c.colour.padStart(6, "0")}`
            : "#000000",
          status: c.status ?? 1,
          isglobal: c.isglobal ?? 0,
        })),
      );
    } catch {
      showError("Failed to fetch categories");
    }
  }, [token, user.company_id, selectedType, role]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // ================= ADD ROW =================
  const handleAddRow = () => {
    if (!selectedType) {
      showError("Select type first");
      return;
    }

    setCategories((prev) => [
      ...prev,
      {
        id: null,
        name: "",
        color: "#000000",
        status: 1,
        isglobal: 0,
      },
    ]);
  };

  const updateField = (index, key, value) => {
    setCategories((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );
  };

  // ================= DELETE =================
  const handleDeleteRow = async (index) => {
    const cat = categories[index];

    if (!cat) return;

    if (!cat.id) {
      setCategories((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (!window.confirm("Delete category?")) return;

    try {
      await axios.delete(
        `${API_URL}/settings/categories/${user.company_id}/${selectedType}/${cat.id}/${role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setCategories((prev) => prev.filter((_, i) => i !== index));
      showSuccess("Deleted Successfully");
    } catch (error) {
      console.error("Delete error:", error);

      let errorMessage = "Delete Failed";

      if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail
            .map((d) => d.msg)
            .join(", ");
        } else {
          errorMessage = error.response.data.detail;
        }
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showError(errorMessage);
    }
  };

  // ================= SAVE =================
  const handleSave = async () => {
    if (!selectedType) {
      showError("Select type first");
      return;
    }

    if (categories.some((c) => !c.name.trim())) {
      showError("Name cannot be empty");
      return;
    }

    setIsSaving(true);

    try {
      const payload = categories.map((c) => ({
        id: c.id ?? 0,
        type_id: selectedType,
        name: c.name.trim(),
        colour: c.color.replace("#", ""),
        status: c.status ?? 0,
        isglobal: canShowGlobalToggle ? (c.isglobal ?? 0) : 0,
      }));

      let response;

      if (isFirstSave) {
        const postURL = `${API_URL}/settings/categories/${user.company_id}/${role}`;

        // Send single object
        response = await axios.post(postURL, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        showSuccess("Category saved successfully");
        fetchCategories();
      } else {
        const putURL = `${API_URL}/settings/categories/${user.company_id}/${selectedType}/${role}`;

        response = await axios.put(putURL, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        showSuccess("Categories updated successfully");
      }

      setCategories(
        (response.data || []).map((c) => ({
          id: c.id,
          name: c.name || "",
          color: c.colour
            ? c.colour.startsWith("#")
              ? c.colour
              : `#${c.colour.padStart(6, "0")}`
            : "#000000",
          status: c.status ?? 1,
          isglobal: c.isglobal ?? 0,
        })),
      );
    } catch (error) {
      console.error(error);
      showError("Save Failed");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ================= UI =================
  return (
    <div className="category-wrapper">
      <div className="category-container">
        <div className="category-header">
          <span className="category-title"></span>

          <div className="category-header-right">
            <select
              className="category-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">Select Type</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="category-search"
            />

            <div className="category-add-btn" onClick={handleAddRow}>
              <img src={plusIcon} alt="add" />
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="category-table-wrapper">
          <div className="category-scroll-wrapper">
            <table className="category-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category Name</th>
                  <th>Color</th>
                  <th>Status</th>
                  {canShowGlobalToggle && <th>Is Global</th>}
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {!selectedType ? (
                  <tr>
                    <td
                      colSpan={canShowGlobalToggle ? 6 : 5}
                      className="table-empty-msg"
                    >
                      Please select a type
                    </td>
                  </tr>
                ) : filteredCategories.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canShowGlobalToggle ? 6 : 5}
                      className="table-empty-msg"
                    >
                      No categories available
                    </td>
                  </tr>
                ) : (
                  filteredCategories.map((c, index) => (
                    <tr key={c.id || index}>
                      <td>{index + 1}</td>

                      <td>
                        <input
                          value={c.name}
                          onChange={(e) =>
                            updateField(index, "name", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="color"
                          value={c.color}
                          onChange={(e) =>
                            updateField(index, "color", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={c.status === 1}
                            onChange={(e) =>
                              updateField(
                                index,
                                "status",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />
                          <span className="slider"></span>
                        </label>
                      </td>

                      {canShowGlobalToggle && (
                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={c.isglobal === 1}
                              onChange={(e) =>
                                updateField(
                                  index,
                                  "isglobal",
                                  e.target.checked ? 1 : 0,
                                )
                              }
                            />
                            <span className="slider"></span>
                          </label>
                        </td>
                      )}

                      <td>
                        <Trash2
                          size={18}
                          className="category-action-icon delete"
                          onClick={() => handleDeleteRow(index)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: "right", padding: "20px" }}>
          <button className="save-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
